<script setup>
import { ref, computed, useSlots, Comment, Text, Fragment } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  // CrmB: forms open as a side panel ("drawer") sliding in from the edge,
  // confirmations stay as a centred dialog. 'auto' picks by type; pass
  // 'drawer' or 'center' to force one.
  presentation: {
    type: String,
    default: 'auto',
    validator: value => ['auto', 'drawer', 'center'].includes(value),
  },
  // Drawer only: drop the body padding and scroll so the content can lay
  // out its own header / scrollable body / footer.
  flush: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value => ['3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(value),
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();
const slots = useSlots();

// Varios diálogos declaran <template #footer /> vacío sólo para suprimir el
// pie por defecto, así que no basta con mirar si el slot existe: hay que ver
// si renderiza algo. Si no, no se pinta el contenedor, que dejaría una franja
// vacía con línea al fondo del panel.
const rendersContent = nodes =>
  (nodes ?? []).some(node => {
    if (node.type === Comment) return false;
    if (node.type === Text) return String(node.children).trim().length > 0;
    if (node.type === Fragment) return rendersContent(node.children);
    return true;
  });

// El pie del panel (borde y espaciado) lo pone el contenedor, para que los
// diálogos que traen su propio #footer no queden pegados al borde. Con slot
// propio manda sólo su contenido: los botones por defecto ya no se pintan.
const hasFooter = () =>
  slots.footer
    ? rendersContent(slots.footer())
    : props.showCancelButton || props.showConfirmButton;

const dialogRef = ref(null);
const dialogContentRef = ref(null);

const isDrawer = computed(() => {
  if (props.presentation === 'drawer') return true;
  if (props.presentation === 'center') return false;
  return props.type === 'edit';
});

const maxWidthClass = computed(() => {
  const classesMap = {
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const open = () => {
  dialogRef.value?.showModal();
};

const close = () => {
  emit('close');
  dialogRef.value?.close();
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      class="w-full transition-all duration-300 ease-in-out shadow-xl"
      :class="[
        maxWidthClass,
        isDrawer
          ? 'dialog--drawer m-0 ltr:ml-auto rtl:mr-auto h-full max-h-full rounded-none overflow-hidden bg-n-alpha-3'
          : [
              'rounded-xl',
              overflowYAuto ? 'overflow-y-auto' : 'overflow-visible',
            ],
      ]"
      @close="close"
    >
      <OnClickOutside :class="isDrawer ? 'h-full' : ''" @trigger="close">
        <form
          ref="dialogContentRef"
          class="flex flex-col w-full text-left align-middle transition-all duration-300 ease-in-out transform bg-n-alpha-3 backdrop-blur-[100px] shadow-xl"
          :class="
            isDrawer
              ? 'h-full gap-0 p-0 overflow-hidden rounded-none'
              : 'h-auto gap-6 p-6 overflow-visible rounded-xl'
          "
          @submit.prevent="confirm"
          @click.stop
        >
          <div
            v-if="title || description"
            class="flex flex-col gap-2"
            :class="{
              'flex-shrink-0 px-6 pt-6 pb-4 border-b border-n-weak': isDrawer,
            }"
          >
            <h3 class="text-base font-medium leading-6 text-n-slate-12">
              {{ title }}
            </h3>
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>
          <!-- In a centred dialog this wrapper is transparent (display:
               contents), so the slot keeps flowing inside the form's gap. -->
          <div
            :class="
              isDrawer
                ? flush
                  ? 'flex flex-col flex-1 min-h-0'
                  : 'flex flex-col flex-1 min-h-0 gap-6 px-6 py-5 overflow-y-auto'
                : 'contents'
            "
          >
            <slot />
            <!-- Dialog content will be injected here -->
          </div>
          <div
            v-if="hasFooter()"
            :class="
              isDrawer
                ? 'flex-shrink-0 px-6 py-4 border-t border-n-weak'
                : 'contents'
            "
          >
            <slot name="footer">
              <div
                v-if="showCancelButton || showConfirmButton"
                class="flex items-center w-full gap-3"
                :class="isDrawer ? 'justify-end' : 'justify-between'"
              >
                <Button
                  v-if="showCancelButton"
                  variant="faded"
                  color="slate"
                  :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                  :class="isDrawer ? '' : 'w-full'"
                  type="button"
                  @click="close"
                />
                <Button
                  v-if="showConfirmButton"
                  :color="type === 'edit' ? 'blue' : 'ruby'"
                  :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                  :class="isDrawer ? '' : 'w-full'"
                  :is-loading="isLoading"
                  :disabled="disableConfirmButton || isLoading"
                  type="submit"
                />
              </div>
            </slot>
          </div>
        </form>
      </OnClickOutside>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
dialog::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

/* Entrance effect: the panel slides in from the inline-end edge. */
@keyframes dialogDrawerIn {
  from {
    transform: translateX(100%);
    opacity: 0.5;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes dialogDrawerInRtl {
  from {
    transform: translateX(-100%);
    opacity: 0.5;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes dialogBackdropIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

dialog.dialog--drawer[open] {
  animation: dialogDrawerIn 0.22s cubic-bezier(0.32, 0.72, 0, 1);
}

[dir='rtl'] dialog.dialog--drawer[open] {
  animation-name: dialogDrawerInRtl;
}

dialog.dialog--drawer[open]::backdrop {
  animation: dialogBackdropIn 0.22s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  dialog.dialog--drawer[open],
  dialog.dialog--drawer[open]::backdrop {
    animation: none;
  }
}
</style>
